import { ProductForSaleDTO, SoldProductDTO, AddinRoleDTO, UsePreparationType, AddinRoleAdditionMode, SoldProductType } from 'core/api';
import { buildSoldProduct } from '../buildSoldProduct';
import { getPrice } from './getPrice';
import { Result } from 'core/infrastructure';

type RoleAddinEditionMode = 'add' | 'remove';

type AddinsByRole = { [roleId: number]: SoldProductDTO[] };

export type Composition = Readonly<{
	currentRole: AddinRoleDTO;
	currentRoleAvailableAddins: ProductForSaleDTO[];
	selectedAddinsByRole: AddinsByRole;
	roles: AddinRoleDTO[];
	ticketId: number;
	addinMenuGroupName: string;
	mainProduct: ProductForSaleDTO;
	mode: RoleAddinEditionMode;
}>;

export type BuildCompositionParams = {
	ticketId: number,
	mainProduct: ProductForSaleDTO,
	roles: AddinRoleDTO[],
	previousAddins: SoldProductDTO[],
	addinMenuGroupName: string
};

type GetAddinsByRoleFn = (roleId: number, ticketId: number, isForMenu: boolean) => Promise<ProductForSaleDTO[]>;

export function createEmptyAddinComposition(mainProduct: ProductForSaleDTO): Composition {
	return {
		currentRole: { AdditionMode: AddinRoleAdditionMode.ChooseDifferentAddinPerUnit, Id: 0, MaxAddins: 0, MinAddins: 0, Name: '', UsePreparationType: UsePreparationType.FromMain, AllowRepeat: true },
		addinMenuGroupName: '',
		currentRoleAvailableAddins: [],
		mainProduct,
		mode: 'add',
		roles: [],
		selectedAddinsByRole: {},
		ticketId: 0
	};
}

export function getQuantity({ selectedAddinsByRole }: Composition, roleId: number, addin: ProductForSaleDTO): number {
	return selectedAddinsByRole[roleId].filter(x => x.SaleFormatId === addin.SaleFormatId).length;
}

export function isCompositionCompleted({ roles, selectedAddinsByRole }: Composition) {
	const notCompletedRoles = roles.filter(x => x.MaxAddins !== selectedAddinsByRole[x.Id].length);
	return notCompletedRoles.length === 0 && Object.entries(selectedAddinsByRole).length > 0;
}

export function isCurrentRoleCompleted({ currentRole, selectedAddinsByRole }: Composition) {
	return currentRole.MaxAddins <= selectedAddinsByRole[currentRole.Id].length;
}

export function getNotCompletedRolesByMinAddins({ roles, selectedAddinsByRole }: Composition) {
	return roles.filter(x => x.MinAddins > 0 && selectedAddinsByRole[x.Id].length < x.MinAddins);
}

export function getNotCompletedRolesByAllAddins({ roles, selectedAddinsByRole }: Composition) {
	return roles.filter(x => x.MinAddins > 0 && selectedAddinsByRole[x.Id].length < x.MaxAddins && x.AdditionMode === AddinRoleAdditionMode.ChooseDifferentAddinPerUnit);
}

export function getNextRole({ currentRole, selectedAddinsByRole, roles }: Composition): Nullable<AddinRoleDTO> {
	if (currentRole.MaxAddins > selectedAddinsByRole[currentRole.Id].length)
		return currentRole;

	const notCompletedRoles = roles.filter((x, idx) => x.MaxAddins !== selectedAddinsByRole[x.Id].length && idx > roles.indexOf(currentRole));
	return notCompletedRoles.length ? notCompletedRoles[0] : null;
}

export async function buildComposition({ roles, ticketId, addinMenuGroupName, previousAddins, mainProduct }: BuildCompositionParams, getAddins: GetAddinsByRoleFn): Promise<Composition> {

	const hasAddinMenuGroup = !!addinMenuGroupName;

	const selectedAddinsByRole: AddinsByRole = roles.reduce((acc, role) => {
		acc[role.Id] = [];
		return acc;
	}, {} as AddinsByRole);

	// Evito llamar al servidor a menos que tenga addins previos que restaurar
	if (previousAddins.length) {

		let remainingAddins = [...previousAddins];

		const availableAddinsByRole = await Promise.all(
			roles.map(role => getAddins(role.Id, ticketId, hasAddinMenuGroup))
		);

		roles.forEach((role, index) => {

			const availableAddins = availableAddinsByRole[index];
			const availableSaleFormatIds = new Set(availableAddins.map(addin => addin.SaleFormatId));
			const restorableAddins = remainingAddins.filter(previousAddin => availableSaleFormatIds.has(previousAddin.SaleFormatId));

			// Si se autocompleta como minimo tengo que coger los MaxAddins o MinAddins, porque en ese caso MaxAddins == MinAddins. Si no se autocompleta, pueden pasar
			// dos cosas, que se permitan repitidos o no. Si se permiten repetidos, me la juego y trato de coger hasta maxAddins, si no, hasta los añadidos disponibles que
			// tengo para ese role (todo esto es una ñapa, como no tenemos fijado a que role va cada añadido en el dominio lo vamos rellenando como podemos, no funcionará bien del todo como
			// antes, lo único que hacemos es intentar rellenar de la mejor manera que sabemos con lo que tenemos)
			const minAddinsCountToRestore = role.MaxAddins === role.MinAddins ? role.MaxAddins : role.AllowRepeat ? role.MaxAddins : availableAddins.length;
			const maxAddinsToRestore = Math.min(role.MaxAddins, minAddinsCountToRestore);

			const selectedAddins = restorableAddins.slice(0, maxAddinsToRestore);
			selectedAddinsByRole[role.Id] = selectedAddins;

			// Los addins ya restaurados no se intentan reutilizar en los siguientes roles.
			remainingAddins = remainingAddins.except(selectedAddins);
		});
	}

	const currentRole = roles[0];
	return {
		mode: 'add',
		currentRole,
		currentRoleAvailableAddins: await getAddins(roles[0].Id, ticketId, hasAddinMenuGroup),
		selectedAddinsByRole,
		roles,
		ticketId,
		mainProduct,
		addinMenuGroupName
	};
}

export async function tryToComplete(composition: Composition, getAddins: GetAddinsByRoleFn): Promise<Composition> {

	let newComposition = { ...composition };

	while (!isCompositionCompleted(newComposition)) {

		const role = newComposition.currentRole;

		// Si el número de addins es variable, no se puede autocompletar
		if (role.MinAddins !== role.MaxAddins)
			return newComposition;

		const requiredAddins = role.MaxAddins;

		const products = await getAddins(role.Id, composition.ticketId, !!composition.addinMenuGroupName);
		// Puedo autocompletar si los addins es igual a la cantidad que tengo que rellenar y no se puede repetir, si se puede repetir el usuario debería decidir que coger.
		// Si puedo repetir sólo autocompleto si hay un añadido, si no el usuario debería elegir
		const canAutoComplete = role.AllowRepeat ? products.length === 1 : products.length === requiredAddins;

		if (!canAutoComplete)
			return newComposition;

		// Si el único añadico posible necesita que se le indique el precio, no se puede autocompletar
		if (products.some(x => !x.HasFixedPrice))
			return newComposition;

		// Si el único añadido posible necesita lote, no se puede autocompletar
		if (products.some(x => x.HasLotControlOnSale))
			return newComposition;

		// Si el único añadido que hay es una entrada y requiere entrada tampoco puedo porque habría que leerla
		if (products.some(x => x.RequiresCouponCode && x.CouponCode === ''))
			return newComposition;

		for (let i = 0; i < products.length; i++) {
			newComposition = await addAddin(newComposition, products[i], getAddins, getPrice);
		}
	}

	return newComposition;
}

export async function setAddinQuantity(composition: Composition, addin: ProductForSaleDTO, quantity: number, getAddins: GetAddinsByRoleFn): Promise<Composition> {
	// Elimino el addin
	let newComposition = composition;
	while (getQuantity(newComposition, composition.currentRole.Id, addin) > 0)
		newComposition = removeAddin(newComposition, addin);

	const currentRole = composition.currentRole;
	// Lo añado las veces que me han indicado
	for (let i = 0; i < quantity; i++) {
		newComposition = await addAddin(newComposition, addin, getAddins,
			// Para obtener el precio si ya he añadido el producto y tiene precio establecido, le devuelvo ese precio, en vez de andar preguntando cada vez
			// que lo añado
			async (product: ProductForSaleDTO) => {
				const price = composition.selectedAddinsByRole[currentRole.Id].find(x => x.SaleFormatId === product.SaleFormatId)?.Price;
				return price !== undefined ? Result.ok(price) : await getPrice(product);
			});
	}

	return newComposition;
}

// OJO: Esto está sacando ventanas a través del createSoldProduct. ¿Tiene sentido que este componente que trata de
// disfrazarse de funciones puras tratando con datos acaba sacando ventanas? Podríamos hacer que el addAddin recibiese
// un soldProduct y sea el consumidor de esta función el que asuma la responsabilidad de obtener el soldProduct a partir
// del productForSale; seguramente quedaría más limpio porque hablarían ventanas con ventanas, pero también es cierto
// que pierdes parte de simplicidad de uso.
export async function addAddin(composition: Composition, addin: ProductForSaleDTO, getAddins: GetAddinsByRoleFn, getPrice: (product: ProductForSaleDTO) => Promise<Result<number>>): Promise<Composition> {

	const role = composition.currentRole;
	const newSelectedAddinsByRole = { ...composition.selectedAddinsByRole };

	const currentAddedAddins = newSelectedAddinsByRole[role.Id];

	if (currentAddedAddins.length < role.MaxAddins) {
		const preparationTypeId = role.UsePreparationType === UsePreparationType.FromMain ? composition.mainProduct.PreparationTypeId : addin.PreparationTypeId;
		const preparationOrderId = composition.mainProduct.PreparationOrderId;
		const priceResult = await getPrice(addin);
		if (!priceResult.ok)
			return composition;

		const soldAddin: SoldProductDTO = buildSoldProduct({
			product: addin,
			groupName: composition.addinMenuGroupName,
			preparationOrderId: preparationOrderId,
			preparationTypeId: preparationTypeId,
			price: priceResult.value,
			lot: { Id: 0, Number: '' },
			type: composition.addinMenuGroupName ? SoldProductType.MenuItem : SoldProductType.Standard,
			size: { Id: 0, Name: '', Priority: 0 },
			color: { Id: 0, Name: '', Priority: 0, Value: '' }
		});

		currentAddedAddins.push(soldAddin);
	}

	newSelectedAddinsByRole[role.Id] = currentAddedAddins;

	const newComposition: Composition = {
		...composition,
		mode: 'add',
		selectedAddinsByRole: newSelectedAddinsByRole
	};

	const nextRole = getNextRole(newComposition);
	return nextRole
		? await selectRole(newComposition, nextRole, getAddins)
		: newComposition;
}

export function removeAddin(composition: Composition, addin: ProductForSaleDTO): Composition {

	const role = composition.currentRole;
	const newResult = { ...composition.selectedAddinsByRole };
	const currentAddedAddins = newResult[role.Id];

	const addinToRemove = currentAddedAddins.find(x => x.SaleFormatId === addin.SaleFormatId);
	if (addinToRemove)
		currentAddedAddins.remove(addinToRemove);

	newResult[role.Id] = currentAddedAddins;

	return {
		...composition,
		mode: 'add',
		selectedAddinsByRole: newResult,
	};
}

export function clearAddins(composition: Composition): Composition {

	const newResult = { ...composition.selectedAddinsByRole };

	Object.keys(newResult).forEach(k => {
		newResult[parseInt(k)] = [];
	});

	return {
		...composition,
		mode: 'add',
		selectedAddinsByRole: newResult
	};
}

export function getPendingAddins(composition: Composition): number {
	return composition.currentRole.MaxAddins - composition.selectedAddinsByRole[composition.currentRole.Id].length;
}

export async function selectRole(composition: Composition, role: AddinRoleDTO, getAddins: GetAddinsByRoleFn): Promise<Composition> {

	const addins = await getAddins(role.Id, composition.ticketId, !!composition.addinMenuGroupName);

	return {
		...composition,
		mode: 'add',
		currentRole: role,
		currentRoleAvailableAddins: addins,
	};
}

export function setAddinsEditionMode(composition: Composition, mode: RoleAddinEditionMode) {
	return {
		...composition,
		mode,
	};
}

export function getAddins(composition: Composition): SoldProductDTO[] {
	return composition.roles.flatMap(role => composition.selectedAddinsByRole[role.Id]);
}


